import type {
  C1_AxisData,
  OpeningKind,
  C1_OpeningData,
  WallSide,
} from "../types/C1_ModelTypes";
import { RoomType, isSpecialExteriorRoomType } from "../types/C1_RoomTypes";
import type { Rect } from "../types/C1_AppTypes";
import type { C1_Project } from "../model/C1_Project";
import { findOpeningInFloor, type C1_Floor } from "../model/C1_Floor";
import type { C1_Room as C1_RoomObject } from "../model/C1_Room";
import { doorReflectForWallCursor } from "./C1_autoOpenings";
import { polygonContainsPoint } from "./C1_polygonPlacement";
import type { C1_Wall } from "../model/C1_Wall";


export const BASE_PX = 70;
export const SNAP = 0.1;
const AUTO_REFLECT_SNAP_DISTANCE = 0.5;
const DOOR_SNAP_OFFSET = 0.15;
export const AXIS_MERGE_DISTANCE = 0.1;
export const MIN_SIZE = 1.0;
export const PAD = 1.8;
export const WALL = 0.18;
export const VIEW_SHIFT = 1;

export const ROOM_TYPE_OPTIONS: RoomType[] = Object.values(RoomType);

export const uid = (prefix: string) => `${prefix}_${Math.random().toString(36).slice(2, 9)}`;
export const round = (value: number) => Math.round(value * 10) / 10;
export const roundToCentimeter = (value: number) => Math.round(value * 100) / 100;

const clamp = (value: number, min: number, max: number) => Math.min(Math.max(value, min), max);

export function snapDoorOffsetToWallEnd(
  offset: number,
  wallSpan: number,
  doorWidth: number,
  disabled = false,
  snapOffset = DOOR_SNAP_OFFSET,
  adjacentWallThickness = { start: 0, end: 0 },
): number {
  if (disabled || wallSpan <= 0.001) return offset;

  const halfWidth = Math.min(doorWidth / 2, wallSpan / 2);
  const distanceFromStart = offset * wallSpan;
  const distanceFromEnd = (1 - offset) * wallSpan;
  if (distanceFromStart - halfWidth <= AUTO_REFLECT_SNAP_DISTANCE) {
    const snappedCenterDistance = Math.min(wallSpan / 2, halfWidth + snapOffset + adjacentWallThickness.start / 2);
    return snappedCenterDistance / wallSpan;
  }
  if (distanceFromEnd - halfWidth <= AUTO_REFLECT_SNAP_DISTANCE) {
    const snappedCenterDistance = Math.min(wallSpan / 2, halfWidth + snapOffset + adjacentWallThickness.end / 2);
    return 1 - snappedCenterDistance / wallSpan;
  }
  return offset;
}

export function adjacentWallThicknessesForDoorSnap(
  project: C1_Project,
  room: C1_RoomObject,
  wall: C1_Wall,
): { start: number; end: number } {
  const walls = room.walls;
  const index = walls?.indexOf(wall) ?? -1;
  if (!walls || index < 0) return { start: 0, end: 0 };
  const thickness = (adjacentWall: C1_Wall) =>
    room.isExteriorSegment(adjacentWall)
      ? project.settings.wallThicknessExterior
      : project.settings.wallThicknessInterior;

  return {
    start: thickness(walls.at((index + walls.size - 1) % walls.size)!),
    end: thickness(walls.at((index + 1) % walls.size)!),
  };
}

export function viewFromBounds(rect: Rect): Rect {
  return {
    x1: rect.x1 - PAD - VIEW_SHIFT,
    y1: rect.y1 - PAD - VIEW_SHIFT,
    x2: rect.x2 + PAD + 1,
    y2: rect.y2 + PAD + 1,
  };
}

const OPENING_STEP = 0.1; // 10 cm

export function moveOpeningToPoint(
  project: C1_Project,
  floor: C1_Floor,
  openingId: string,
  point: { x: number; y: number },
  snap = false,
  currentReflect?: boolean,
  disableAutoReflectSnap = false,
): C1_OpeningData | null {
  const opening = findOpeningInFloor(floor, openingId);
  if (!opening) {
    return null;
  }

  const room = floor.rooms.find((entry) => entry.id === opening.roomId);
  if (!room) {
    return null;
  }

  const placement = room.nearestWallPlacementInRoom(point, opening.width, snap);
  if (!placement) return null;
  const wall = room.walls?.findFirst((candidate) => candidate.id === placement.wallId);
  const wallPoints = wall ? room.wallPoints(wall) : undefined;
  if (!wall || !wallPoints) return null;
  const span = Math.hypot(wallPoints.end.x - wallPoints.start.x, wallPoints.end.y - wallPoints.start.y);
  const side = placement.side;
  let clampedOffset = placement.offset;
  if (opening.kind === "door") {
    clampedOffset = snapDoorOffsetToWallEnd(
      clampedOffset,
      span,
      opening.width,
      disableAutoReflectSnap,
      DOOR_SNAP_OFFSET,
      adjacentWallThicknessesForDoorSnap(project, room, wall),
    );
  }

  return {
    ...opening.toJSON(),
    // Transient placement metadata; roomId is omitted again by C1_Opening.toJSON().
    roomId: opening.roomId,
    side,
    _wallId: placement.wallId,
    offset: clampedOffset,
    reflect:
      opening.kind === "door"
        ? doorReflectForWallCursor(wallPoints.start, wallPoints.end, point, currentReflect ?? opening.reflect)
        : opening.reflect,
    mode: "manual",
  };
}

type WallPlacement = {
  room: C1_RoomObject;
  side: WallSide;
  wallId: string;
  offset: number;
};

export function openingWidthFromKind(kind: OpeningKind) {
  if (kind === "door") return 0.9;
  if (kind === "garage-door") return 2.5;
  return 1.2;
}

export function doorReflectForOpeningCursor(room: C1_RoomObject, wallId: string, cursor: { x: number; y: number }, currentReflect = false): boolean {
  const wall = room.walls?.findFirst(candidate => candidate.id === wallId);
  const points = wall ? room.wallPoints(wall) : undefined;
  return points ? doorReflectForWallCursor(points.start, points.end, cursor, currentReflect) : currentReflect;
}

function roomContainingPoint(
  floor: C1_Floor,
  point: { x: number; y: number },
): C1_RoomObject | null {
  const candidates = floor.rooms.map(room => ({ room, polygon: room.polygonPoints() }))
    .filter(({ polygon }) => polygonContainsPoint(polygon, point));
  // Shared wall axes belong to both polygons. Never let collection order
  // make a surrounding L-shaped room capture every door on that boundary.
  const interior = candidates.filter(({ polygon }) => polygonContainsPoint(polygon, point, false));
  const area = (polygon: Array<{ x: number; y: number }>) => Math.abs(polygon.reduce((sum, vertex, index) => {
    const next = polygon[(index + 1) % polygon.length];
    return sum + vertex.x * next.y - next.x * vertex.y;
  }, 0));
  return (interior.length ? interior : candidates)
    .sort((left, right) => area(left.polygon) - area(right.polygon) || left.room.id.localeCompare(right.room.id))[0]?.room ?? null;
}

function nearestWallPlacement(
  project: C1_Project,
  floor: C1_Floor,
  point: { x: number; y: number },
  openingWidth: number,
  rooms: C1_RoomObject[] = floor.rooms,
): WallPlacement | null {
  let best: {
    room: C1_RoomObject;
    side: WallSide;
    wallId: string;
    offset: number;
    distance: number;
  } | null = null;

  for (const room of rooms) {
    const placement = room.nearestWallPlacementInRoom(point, openingWidth);
    if (!placement) {
      continue;
    }

    const center = room.wallPointAt(placement.wallId, placement.offset);
    if (!center) continue;
    const distance = Math.hypot(point.x - center.x, point.y - center.y);

    if (!best || distance < best.distance) {
      best = {
        room: placement.room,
        side: placement.side,
        wallId: placement.wallId,
        offset: placement.offset,
        distance,
      };
    }
  }

  if (!best) {
    return null;
  }

  return { room: best.room, side: best.side, wallId: best.wallId, offset: best.offset };
}

export function previewManualOpeningPlacement(
  project: C1_Project,
  floor: C1_Floor,
  kind: OpeningKind,
  point: { x: number; y: number },
  disableAutoReflectSnap = false,
  openingWidth = openingWidthFromKind(kind),
): WallPlacement | null {
  let placement: WallPlacement | null;
  if (kind === "door") {
    const room = roomContainingPoint(floor, point);
    if (!room) {
      placement = nearestWallPlacement(project, floor, point, openingWidth);
    } else {
      placement = room.nearestWallPlacementInRoom(point, openingWidth);
    }
  } else if (kind === "garage-door") {
    const garageRooms = floor.rooms.filter(
      (room) => room.type === RoomType.Garage || room.type === RoomType.DetachedGarage,
    );
    placement = nearestWallPlacement(project, floor, point, openingWidth, garageRooms);
  } else {
    placement = nearestWallPlacement(project, floor, point, openingWidth);
    // Shared exterior walls belong to the indoor room when placing a window.
    if (placement && isSpecialExteriorRoomType(placement.room.type)) {
      const wallId = placement.wallId;
      const wall = placement.room.walls?.findFirst((candidate) => candidate.id === wallId);
      const partnerWall = wall?.partnerWall;
      const partnerRoom = partnerWall?.parent?.c1 as C1_RoomObject | undefined;
      const center = placement.room.wallPointAt(placement.wallId, placement.offset);
      const points = partnerWall && partnerRoom?.wallPoints(partnerWall);
      if (partnerWall && partnerRoom?.className === "C1_Room" && !isSpecialExteriorRoomType(partnerRoom.type) && center && points) {
        const dx = points.end.x - points.start.x;
        const dy = points.end.y - points.start.y;
        const lengthSquared = dx * dx + dy * dy;
        if (lengthSquared > 0) {
          placement = {
            room: partnerRoom,
            side: partnerRoom.compatibilitySide(partnerWall),
            wallId: partnerWall.id,
            offset: clamp(((center.x - points.start.x) * dx + (center.y - points.start.y) * dy) / lengthSquared, 0, 1),
          };
        }
      }
    }
  }

  if (!placement || kind !== "door" || disableAutoReflectSnap) return placement;

  const room = placement.room;
  const wall = room.walls?.findFirst((candidate) => candidate.id === placement.wallId);
  const points = wall ? room.wallPoints(wall) : undefined;
  if (!wall || !points) return placement;
  const span = Math.hypot(points.end.x - points.start.x, points.end.y - points.start.y);
  if (span <= 0.001) return placement;

  return {
    ...placement,
    offset: snapDoorOffsetToWallEnd(
      placement.offset,
      span,
      openingWidth,
      false,
      DOOR_SNAP_OFFSET,
      adjacentWallThicknessesForDoorSnap(project, room, wall),
    ),
  };
}

export { evalExpression } from "./C1_evalExpression";
